/**
 * «Наличные → Кассы»: наличные кассы салонов по дням — сколько было, пришло,
 * ушло, осталось, сколько насчитали руками и на сколько не сошлось.
 *
 * Остаток и расхождение считаются как в экране сверки CRM
 * (`kassaCheckList.php`): от пересчёта к пересчёту, «было + оплаты − выплаты».
 * Опечатка в пересчёте и её исправление дают пару ±, за день она гасится —
 * поэтому расхождение показывается суммой за день и за период.
 *
 * Клик по «пришло» / «ушло» открывает сами оплаты и выплаты (с их статьёй и
 * получателем) на экране «Кассы CRM → Кассы».
 * Спека: `docs/specs/2026-10-02-cash-kassa-check-design.md`.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Combobox } from "@/components/Combobox";
import { DateRangePicker, withPeriod, type Окно } from "@/components/DateRangePicker";
import { NoAccess } from "@/components/NoAccess";
import { KASSA, useCan } from "@/lib/access";
import { isValidDate, iso, plusDays } from "@/lib/dates";
import { type CashCheck, type CashDay, type CashKassa, type CashReport, type Kassa, loadCash, loadKassas } from "@/lib/kassa";
import { money, type Money } from "@/lib/pnl";

const TH = "border-b border-border px-2.5 py-2 text-right font-medium";
const TD = "border-b border-border px-2.5 py-1.5 text-right tabular-nums";

export function CashPage() {
  return useCan(KASSA) ? <CashScreen /> : <NoAccess what="наличным кассам" />;
}

/** Окна экрана: кассу смотрят неделями. */
function windows(): Окно[] {
  const today = new Date();
  const monday = plusDays(today, -((today.getDay() + 6) % 7));
  const y = today.getFullYear();
  const m = today.getMonth();
  return [
    { label: "эта неделя", hint: "с понедельника по сегодня", from: iso(monday), to: iso(today) },
    { label: "прошлая неделя", hint: "понедельник — воскресенье", from: iso(plusDays(monday, -7)), to: iso(plusDays(monday, -1)) },
    { label: "этот месяц", hint: "с первого числа по сегодня", from: iso(new Date(y, m, 1)), to: iso(today) },
    { label: "прошлый месяц", hint: "весь прошлый месяц", from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
  ];
}

/** «28.09 пн». */
function dayLabel(value: string): string {
  const d = new Date(`${value}T00:00:00`);
  return `${value.slice(8, 10)}.${value.slice(5, 7)} ${["вс", "пн", "вт", "ср", "чт", "пт", "сб"][d.getDay()]}`;
}

/** «28.09 20:51». */
function moment(isoValue: string): string {
  const [day, time] = isoValue.split("T");
  return `${day?.slice(8, 10)}.${day?.slice(5, 7)} ${time?.slice(0, 5) ?? ""}`;
}

function balance(value: Money | null) {
  return value === null ? <span className="text-fg-faint" title="касса ещё не пересчитана — остатка не знаем">?</span> : money(value);
}

/** Расхождение: минус — недостача (красным), плюс — излишек. */
function Diff({ value }: { value: Money | null }) {
  const n = Number(value ?? 0);
  if (!n) return <span className="text-fg-faint">—</span>;
  return (
    <span className={n < 0 ? "font-medium text-err" : "font-medium text-warn"} title={n < 0 ? "недостача" : "излишек"}>
      {n > 0 ? "+" : "−"}
      {money(Math.abs(n))}
    </span>
  );
}

function CashScreen() {
  const [params, setParams] = useSearchParams();
  const [kassas, setKassas] = useState<Kassa[]>([]);
  const [report, setReport] = useState<CashReport | null>(null);
  const [failure, setFailure] = useState("");
  const [open, setOpen] = useState<Set<number>>(new Set());

  const week = windows()[0]!;
  const from = isValidDate(params.get("date_from") ?? "") ? params.get("date_from")! : week.from;
  const to = isValidDate(params.get("date_to") ?? "") ? params.get("date_to")! : week.to;
  const kassaId = params.get("kassa_id") ? Number(params.get("kassa_id")) : undefined;

  useEffect(() => {
    loadKassas()
      .then((all) => setKassas(all.filter((k) => k.kind === "cash")))
      .catch((error: Error) => setFailure(error.message));
  }, []);

  useEffect(() => {
    setReport(null);
    setFailure("");
    loadCash({ date_from: from, date_to: to, kassa_id: kassaId })
      .then((r) => {
        setReport(r);
        // Касса с расхождением раскрыта сразу: за ней и пришли.
        setOpen(new Set(r.kassas.filter((k) => Number(k.diff) !== 0).map((k) => k.kassa_id)));
      })
      .catch((error: Error) => setFailure(error.message));
  }, [from, to, kassaId]);

  const toggle = (id: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const issues = useMemo(() => (report?.kassas ?? []).filter((k) => Number(k.diff) !== 0), [report]);

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <DateRangePicker
          label="период"
          from={from}
          to={to}
          windows={windows()}
          onChange={(f, t) => setParams(withPeriod(params, f, t))}
        />
        <Combobox
          className="max-w-[240px]"
          value={String(kassaId ?? "")}
          onChange={(v) => {
            const next = new URLSearchParams(params);
            if (v) next.set("kassa_id", v);
            else next.delete("kassa_id");
            setParams(next);
          }}
          options={[{ value: "", label: "Все салоны" }, ...kassas.map((k) => ({ value: String(k.id), label: k.name }))]}
        />
        {report?.kassas.length ? (
          <button
            type="button"
            className="ml-auto text-fg-muted hover:text-fg"
            onClick={() =>
              setOpen(open.size === report.kassas.length ? new Set() : new Set(report.kassas.map((k) => k.kassa_id)))
            }
          >
            {open.size === report.kassas.length ? "свернуть все" : "развернуть все"}
          </button>
        ) : null}
      </div>

      <div className="text-[12px] text-fg-faint">
        Остаток — от пересчёта к пересчёту: «насчитали + оплаты − выплаты», как в сверке CRM. Расхождение: минус —
        недостача, плюс — излишек; опечатка и её исправление за день гасятся.
      </div>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {report && issues.length ? (
        <div data-annot="расхождения" className="rounded-[var(--radius-ui)] border border-warn/40 px-3 py-2 text-[12.5px]">
          <div className="mb-1 font-medium">Не сошлось за период</div>
          <ul className="grid gap-0.5">
            {issues.map((k) => (
              <li key={k.kassa_id}>
                <button type="button" className="hover:underline" onClick={() => setOpen(new Set([...open, k.kassa_id]))}>
                  {k.name}
                </button>
                : <Diff value={k.diff} /> ₽
              </li>
            ))}
          </ul>
        </div>
      ) : report ? (
        <div className="text-[12.5px] text-ok">За период все пересчёты сошлись.</div>
      ) : null}

      {report === null ? (
        failure ? null : <div className="text-[12.5px] text-fg-muted">…</div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] text-fg-muted">
                <th className={`${TH} text-left`}>салон · день</th>
                <th className={TH}>на начало</th>
                <th className={TH}>пришло</th>
                <th className={TH}>ушло</th>
                <th className={TH}>на конец</th>
                <th className={TH}>пересчёт</th>
                <th className={TH}>расхождение</th>
              </tr>
            </thead>
            <tbody>
              {report.kassas.map((k) => (
                <KassaRows key={k.kassa_id} kassa={k} from={from} to={to} open={open.has(k.kassa_id)} onToggle={() => toggle(k.kassa_id)} />
              ))}
              <tr className="bg-bg-subtle font-medium">
                <td className={`${TD} text-left`}>Итого</td>
                <td className={TD} />
                <td className={`${TD} text-ok`}>+{money(report.total.income)}</td>
                <td className={TD}>−{money(report.total.expense)}</td>
                <td className={TD}>{money(report.total.closing)}</td>
                <td className={TD} />
                <td className={TD}>
                  <Diff value={report.total.diff} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

/** Ссылка на оплаты или выплаты кассы за дни — там видно, что именно это были за деньги. */
function flowLink(kassaId: number, from: string, to: string, payments: boolean): string {
  const p = new URLSearchParams({ kassa_id: String(kassaId), date_from: from, date_to: to });
  if (payments) p.set("flow", "payments");
  else p.set("kind", "cash");
  return `/kassa?${p}`;
}

function KassaRows(props: { kassa: CashKassa; from: string; to: string; open: boolean; onToggle: () => void }) {
  const { kassa: k, from, to } = props;
  return (
    <>
      <tr data-annot={`касса ${k.name}`} className="cursor-pointer hover:bg-bg-hover" onClick={props.onToggle}>
        <td className={`${TD} text-left font-medium`}>
          <span className="inline-flex items-center gap-1">
            {props.open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {k.name}
          </span>
        </td>
        <td className={TD}>{balance(k.opening)}</td>
        <td className={TD} onClick={(e) => e.stopPropagation()}>
          <Link to={flowLink(k.kassa_id, from, to, true)} className="text-ok hover:underline">
            +{money(k.income)}
          </Link>
        </td>
        <td className={TD} onClick={(e) => e.stopPropagation()}>
          <Link to={flowLink(k.kassa_id, from, to, false)} className="hover:underline">
            −{money(k.expense)}
          </Link>
        </td>
        <td className={`${TD} font-medium`}>{balance(k.closing)}</td>
        <td className={`${TD} text-[11.5px] text-fg-muted`}>
          {k.last_check ? (
            <>
              {money(k.last_check.amount)} <span className="text-fg-faint">· {moment(k.last_check.date)}</span>
            </>
          ) : (
            "не пересчитывали"
          )}
        </td>
        <td className={TD}>
          <Diff value={k.diff} />
        </td>
      </tr>
      {props.open ? k.days.map((d) => <DayRows key={d.date} kassaId={k.kassa_id} day={d} />) : null}
    </>
  );
}

function DayRows({ kassaId, day }: { kassaId: number; day: CashDay }) {
  const [checksOpen, setChecksOpen] = useState(Number(day.diff) !== 0);
  const quiet = !Number(day.income) && !Number(day.expense) && !day.checks.length;
  return (
    <Fragment>
      <tr data-annot={`день ${day.date}`} className={`text-[12px] ${quiet ? "text-fg-faint" : ""}`}>
        <td className={`${TD} pl-8 text-left`}>{dayLabel(day.date)}</td>
        <td className={TD}>{balance(day.opening)}</td>
        <td className={TD}>
          {Number(day.income) ? (
            <Link to={flowLink(kassaId, day.date, day.date, true)} className="text-ok hover:underline">
              +{money(day.income)}
            </Link>
          ) : (
            "—"
          )}
        </td>
        <td className={TD}>
          {Number(day.expense) ? (
            <Link to={flowLink(kassaId, day.date, day.date, false)} className="hover:underline" title="выплаты дня: статья и получатель">
              −{money(day.expense)}
            </Link>
          ) : (
            "—"
          )}
        </td>
        <td className={TD}>{balance(day.closing)}</td>
        <td className={TD}>
          {day.checks.length ? (
            <button type="button" className="text-fg-muted hover:text-fg hover:underline" onClick={() => setChecksOpen(!checksOpen)}>
              {money(day.counted ?? "0")} <span className="text-fg-faint">({day.checks.length})</span>
            </button>
          ) : (
            "—"
          )}
        </td>
        <td className={TD}>
          <Diff value={day.diff} />
        </td>
      </tr>
      {checksOpen
        ? day.checks.map((c) => <CheckRow key={c.crm_id} check={c} />)
        : null}
    </Fragment>
  );
}

function CheckRow({ check: c }: { check: CashCheck }) {
  return (
    <tr data-annot={`сверка CRM ${c.crm_id}`} className="bg-bg-subtle/50 text-[11.5px] text-fg-muted">
      <td className={`${TD} pl-14 text-left`}>
        сверка {moment(c.date).split(" ")[1]}
        {c.comment ? <span className="ml-2 italic">«{c.comment}»</span> : null}
      </td>
      <td className={TD} colSpan={3}>
        ожидали {c.expected === null ? "?" : money(c.expected)}
      </td>
      <td className={TD} />
      <td className={TD}>насчитали {money(c.amount)}</td>
      <td className={TD}>
        <Diff value={c.diff} />
      </td>
    </tr>
  );
}
